import type {
  GpuWakeAction,
  GpuWakeHistoryEntry,
  InfrastructureHealth,
} from '../api.infrastructure';

export type ArmedGpuWake = {
  startedAt: string;
  workId?: number | null;
  timeoutSec: number;
};

type Wake = NonNullable<NonNullable<InfrastructureHealth['gpu']>['wake']>;

function wakeTimeoutSec(wake: Wake): number {
  const raw = wake.timeoutSec;
  return raw != null && Number.isFinite(raw) && raw > 0 ? raw : 600;
}

/** True when wake age has reached GPU_WAKE_TIMEOUT_SEC (default 600s / 10m). */
export function gpuWakePastTimeout(
  data: InfrastructureHealth | undefined,
  nowMs: number = Date.now(),
): boolean {
  const wake = data?.gpu?.wake;
  if (!wake) return false;
  if (wake.timedOut === true) return true;
  const timeoutSec = wakeTimeoutSec(wake);
  if (
    wake.elapsedSec != null &&
    Number.isFinite(wake.elapsedSec) &&
    wake.elapsedSec >= timeoutSec
  ) {
    return true;
  }
  if (wake.enqueuedAt) {
    const started = Date.parse(wake.enqueuedAt);
    if (Number.isFinite(started) && (nowMs - started) / 1000 >= timeoutSec) {
      return true;
    }
  }
  return false;
}

/**
 * Live scale-from-zero wake still within the timeout budget.
 * Timed-out / stuck rows settle back to idle — do not keep counting.
 */
export function gpuWakeInProgress(
  data: InfrastructureHealth | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (data?.gpu?.wake?.active !== true) return false;
  if (data.gpu.wake.noInstance === true) return false;
  return !gpuWakePastTimeout(data, nowMs);
}

function _compactReason(text: string): string {
  const trimmed = text.replace(/^Last wake error[^:]*:\s*/i, '').trim();
  if (!trimmed) return 'failed';
  if (trimmed.length <= 32) return trimmed;
  return `${trimmed.slice(0, 31)}…`;
}

/**
 * Short reason the GPU did not come up (or why Wake is blocked).
 * Shown on the GPU / Wake chips — not only in the tooltip.
 */
export function gpuWakeFailureReason(
  data: InfrastructureHealth | undefined,
  nowMs: number = Date.now(),
): string | undefined {
  if (!data) return undefined;
  if (data.gpu?.status === 'ready' || data.gpu?.ready === true) return undefined;
  if (gpuWakeInProgress(data, nowMs)) return undefined;
  const wake = data.gpu?.wake;
  if (wake?.enabled === false) return 'disabled';
  if (wake?.provisioningEnabled === false) return 'provisioning off';
  if (gpuWakeNoInstance(data, nowMs)) return 'no instance';
  // Stuck active wake: elapsed / enqueuedAt already past the budget even when
  // the server never set timedOut or lastError.
  if (wake?.active === true && gpuWakePastTimeout(data, nowMs)) return 'timed out';
  if (wake?.timedOut === true) return 'timed out';
  const err = wake?.lastError;
  if (err) {
    if (/timed out/i.test(err)) return 'timed out';
    if (_looksLikeCapacityError(err)) return 'no instance';
    return _compactReason(err);
  }
  return undefined;
}

/** Show a manual Wake chip when GPU is down and media-manager can enqueue gpu_wake. */
export function gpuWakeActionAvailable(
  data: InfrastructureHealth | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (!data?.mediaManagerReachable) return false;
  if (data.gpu?.configured === false || data.gpu?.status === 'not_configured') return false;
  if (data.gpu?.status === 'ready') return false;
  if (data.gpu?.wake?.enabled === false) return false;
  // Allow retry after a timed-out / settled wake; hide only while live wake is in the budget.
  return !gpuWakeInProgress(data, nowMs);
}

/**
 * Settled capacity failure (AWS out of GPU inventory), not a live wake ticker.
 * Plain wake timeout settles to Off — not sticky "No instance · Nh".
 */
export function gpuWakeNoInstance(
  data: InfrastructureHealth | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (data?.gpu?.status === 'ready' || data?.gpu?.ready === true) return false;
  const wake = data?.gpu?.wake;
  if (!wake) return false;
  // Timed-out in-flight wake → Off (media-manager should clear active; UI settles either way).
  if (wake.active === true && gpuWakePastTimeout(data, nowMs)) return false;
  if (wake.active === true) return false;
  if (wake.noInstance === true) return true;
  return _looksLikeCapacityError(wake.lastError);
}

/** Compact elapsed since wake enqueue, e.g. `12s`, `3m05s`, `1h02m`. */
export function formatWakeElapsed(
  enqueuedAt: string | null | undefined,
  nowMs: number = Date.now(),
): string | undefined {
  if (!enqueuedAt) return undefined;
  const started = Date.parse(enqueuedAt);
  if (!Number.isFinite(started)) return undefined;
  const sec = Math.max(0, Math.floor((nowMs - started) / 1000));
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}m${String(s).padStart(2, '0')}s`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return `${h}h${String(rm).padStart(2, '0')}m`;
}

export function gpuWakeElapsedLabel(
  data: InfrastructureHealth | undefined,
  nowMs: number = Date.now(),
): string | undefined {
  // Only tick while wake is still in the timeout window.
  if (!gpuWakeInProgress(data, nowMs)) return undefined;
  if (data?.gpu?.wake?.enqueuedAt) {
    return formatWakeElapsed(data.gpu.wake.enqueuedAt, nowMs);
  }
  const elapsed = data?.gpu?.wake?.elapsedSec;
  if (elapsed == null || !Number.isFinite(elapsed)) return undefined;
  if (elapsed < 60) return `${elapsed}s`;
  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  return `${m}m${String(s).padStart(2, '0')}s`;
}

function _compactTypes(types: string[]): string | undefined {
  if (types.length === 0) return undefined;
  // Keep chip compact: first two + overflow count.
  if (types.length <= 2) return types.join(',');
  return `${types.slice(0, 2).join(',')}+${types.length - 2}`;
}

/**
 * Instance type(s) for the GPU chip.
 * Prefer exact NodeClaim type → node labels → NodePool allowlist.
 */
export function gpuRequestedInstanceLabel(
  data: InfrastructureHealth | undefined,
): string | undefined {
  const provisioning = (data?.gpu?.capacity?.provisioningInstanceTypes ?? []).filter(Boolean);
  if (provisioning.length > 0) return _compactTypes(provisioning);
  const running = (data?.gpu?.capacity?.instanceTypes ?? []).filter(Boolean);
  if (running.length > 0) return _compactTypes(running);
  const requested = (data?.gpu?.capacity?.requestedInstanceTypes ?? []).filter(Boolean);
  return _compactTypes(requested);
}

/**
 * True when capacity probe says Karpenter is still launching a GPU node.
 * A timed-out wake leaves capacity stuck on provisioning — settle, do not keep ticking.
 */
export function gpuProvisioningInProgress(
  data: InfrastructureHealth | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (data?.gpu?.capacity?.status !== 'provisioning') return false;
  // Wake budget elapsed — settle to Off; do not keep a NodeClaim clock running.
  if (gpuWakePastTimeout(data, nowMs)) return false;
  return true;
}

/** Elapsed since oldest non-Ready NodeClaim (live tick via nowMs). */
export function gpuProvisioningElapsedLabel(
  data: InfrastructureHealth | undefined,
  nowMs: number = Date.now(),
): string | undefined {
  if (!gpuProvisioningInProgress(data, nowMs)) return undefined;
  const startedAt = data?.gpu?.capacity?.provisioningStartedAt;
  if (startedAt) return formatWakeElapsed(startedAt, nowMs);
  const elapsed = data?.gpu?.capacity?.provisioningElapsedSec;
  if (elapsed == null || !Number.isFinite(elapsed)) return undefined;
  if (elapsed < 60) return `${elapsed}s`;
  const m = Math.floor(elapsed / 60);
  const s = elapsed % 60;
  return `${m}m${String(s).padStart(2, '0')}s`;
}

export function gpuWakeSummary(
  data: InfrastructureHealth | undefined,
  nowMs: number = Date.now(),
): string | undefined {
  const wake = data?.gpu?.wake;
  if (!wake) return undefined;
  const types = gpuRequestedInstanceLabel(data);
  if (gpuWakeInProgress(data, nowMs)) {
    const parts = ['Wake in progress'];
    const elapsed = formatWakeElapsed(wake.enqueuedAt, nowMs) ?? gpuWakeElapsedLabel(data, nowMs);
    if (elapsed) parts.push(elapsed);
    const exact = (data?.gpu?.capacity?.provisioningInstanceTypes ?? []).filter(Boolean);
    if (types) parts.push(exact.length > 0 ? `launching ${types}` : `requesting ${types}`);
    if (wake.state) parts.push(String(wake.state));
    if (wake.workId != null) parts.push(`work #${wake.workId}`);
    return parts.join(' · ');
  }
  // Timed-out stuck active row: settle — no growing clock.
  if (wake.active === true && gpuWakePastTimeout(data, nowMs)) {
    const parts = ['Wake timed out · settled to idle'];
    if (types) parts.push(`requested ${types}`);
    if (wake.workId != null) parts.push(`work #${wake.workId}`);
    return parts.join(' · ');
  }
  if (gpuWakeNoInstance(data, nowMs)) {
    const parts = ['AWS instance not available'];
    if (types) parts.push(`requested ${types}`);
    if (wake.lastError) parts.push(wake.lastError);
    return parts.join(' · ');
  }
  if (wake.lastError) {
    const when = wake.lastErrorAt ? ` (${wake.lastErrorAt})` : '';
    return `Last wake error${when}: ${wake.lastError}`;
  }
  return undefined;
}

function _isCapacityFailure(entry: GpuWakeHistoryEntry): boolean {
  return entry.noInstance === true || _looksLikeCapacityError(entry.error);
}

function _historyCause(entry: GpuWakeHistoryEntry): GpuWakeHistoryEntry | undefined {
  const err = entry.error?.trim();
  if (err || entry.timedOut || entry.state === 'failed') return entry;
  return undefined;
}

/** Recent wake failures, including timeouts and Karpenter errors. */
export function gpuWakeHistoryRows(
  data: InfrastructureHealth | undefined,
): GpuWakeHistoryEntry[] {
  const listed = data?.gpu?.wake?.history;
  const source: GpuWakeHistoryEntry[] =
    listed && listed.length > 0
      ? listed
      : data?.gpu?.wake?.lastError
        ? [
            {
              workId: data.gpu.wake.lastErrorWorkId,
              state: data.gpu.wake.lastErrorState,
              completedAt: data.gpu.wake.lastErrorAt,
              error: data.gpu.wake.lastError,
              timedOut: data.gpu.wake.timedOut,
              noInstance:
                data.gpu.wake.noInstance ?? _looksLikeCapacityError(data.gpu.wake.lastError),
            },
          ]
        : [];
  return source.filter((row) => _historyCause(row) != null).slice(0, 8);
}

/** AWS capacity, Karpenter's error, or the other text that stopped the wake. */
export function gpuWakeHistoryHeadline(entry: GpuWakeHistoryEntry): string {
  if (_isCapacityFailure(entry)) return 'AWS capacity issue';
  const err = entry.error?.trim();
  if (err) return err;
  if (entry.timedOut) return 'Wake timed out';
  return 'Wake failed';
}

/** Full stored error when the headline is a shorter label. */
export function gpuWakeHistoryDetail(entry: GpuWakeHistoryEntry): string | undefined {
  const err = entry.error?.trim();
  if (!err) return undefined;
  if (err === gpuWakeHistoryHeadline(entry)) return undefined;
  return err;
}

export function formatWakeHistoryWhen(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return undefined;
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Queue start from the gpu_wake row — never invent a click-time clock. */
export function wakeStartForAction(
  action: GpuWakeAction | undefined,
  data: InfrastructureHealth | undefined,
  nowMs: number = Date.now(),
): string | undefined {
  const timeoutSec = action?.wake?.timeoutSec ?? data?.gpu?.wake?.timeoutSec ?? 600;
  for (const iso of [action?.wake?.enqueuedAt, data?.gpu?.wake?.enqueuedAt]) {
    if (!iso) continue;
    const started = Date.parse(iso);
    if (Number.isFinite(started) && (nowMs - started) / 1000 < timeoutSec) {
      return iso;
    }
  }
  return undefined;
}

/** Merge POST /gpu/wake into the health cache so the chip flips before refetch. */
export function mergeWakeActionIntoHealth(
  data: InfrastructureHealth | undefined,
  action: GpuWakeAction | undefined,
  nowMs: number = Date.now(),
): InfrastructureHealth | undefined {
  if (!data) return data;
  if (action?.status !== 'enqueued' && action?.status !== 'already_active') return data;
  const timeoutSec = action.wake?.timeoutSec ?? data.gpu?.wake?.timeoutSec ?? 600;
  const startedAt = wakeStartForAction(action, data, nowMs);
  return {
    ...data,
    gpu: {
      ...data.gpu,
      wake: {
        ...data.gpu?.wake,
        ...action.wake,
        active: true,
        timedOut: false,
        noInstance: false,
        // Previous elapsed belongs to the settled wake. gpuWakePastTimeout
        // trusts it before enqueuedAt, so a leftover value at/past timeoutSec
        // would expire this wake immediately.
        elapsedSec: startedAt ? null : (action.wake?.elapsedSec ?? null),
        workId: action.wake?.workId ?? action.workId ?? data.gpu?.wake?.workId,
        enqueuedAt: startedAt ?? action.wake?.enqueuedAt ?? null,
        timeoutSec,
      },
    },
  };
}

/**
 * Keep the chip in Waking after a successful click even if health refetch
 * still looks idle (timed-out snapshot race). Drops after timeout or Ready.
 */
export function applyArmedGpuWake(
  data: InfrastructureHealth | undefined,
  armed: ArmedGpuWake | null,
  nowMs: number = Date.now(),
): InfrastructureHealth | undefined {
  if (!data || !armed) return data;
  if (data.gpu?.status === 'ready' || data.gpu?.ready === true) return data;
  if (gpuWakeInProgress(data, nowMs)) return data;
  const started = Date.parse(armed.startedAt);
  if (!Number.isFinite(started) || (nowMs - started) / 1000 >= armed.timeoutSec) {
    return data;
  }
  const existingStart = data.gpu?.wake?.enqueuedAt;
  const existingMs = existingStart ? Date.parse(existingStart) : NaN;
  const enqueuedAt =
    Number.isFinite(existingMs) && (nowMs - existingMs) / 1000 < armed.timeoutSec
      ? existingStart
      : armed.startedAt;
  return {
    ...data,
    gpu: {
      ...data.gpu,
      wake: {
        ...data.gpu?.wake,
        active: true,
        timedOut: false,
        noInstance: false,
        // Previous elapsed belongs to the settled wake. gpuWakePastTimeout
        // trusts it before enqueuedAt, so a leftover value at/past timeoutSec
        // would expire this wake immediately.
        elapsedSec: null,
        workId: armed.workId ?? data.gpu?.wake?.workId,
        enqueuedAt,
        timeoutSec: armed.timeoutSec,
      },
    },
  };
}

function _looksLikeCapacityError(text: string | null | undefined): boolean {
  if (!text) return false;
  const lowered = text.toLowerCase();
  return (
    lowered.includes('insufficient') ||
    lowered.includes('no instance') ||
    lowered.includes('capacity') ||
    lowered.includes('gpu not ready after wake')
  );
}
